import { CLAIM_MARKER_COLOR, LAYERS, MARKER_COLORS } from "./constants";
import { markerIconId } from "./markerIcons";
import {
  brokerStatsById,
  brokersById,
  claimsByMotor,
  claimsByProperty,
  motorClaims,
  motorsByBroker,
  motorsById,
  propertiesByBroker,
  propertiesById,
  propertyExposures,
  motorExposures,
} from "./portfolioData";

export const formatMoney = (value, language) =>
  new Intl.NumberFormat(language === "el" ? "el-GR" : "en-GB", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value || 0);

export const formatDate = (value, language) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language === "el" ? "el-GR" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

export const postalCodeOf = (entity, layer) =>
  layer === LAYERS.motor ? entity.garage_postal_code : entity.postal_code;

export const coordinatesOf = (entity, layer) =>
  layer === LAYERS.motor
    ? [entity.garage_longitude, entity.garage_latitude]
    : [entity.longitude, entity.latitude];

export const labelOf = (entity, layer) => {
  if (layer === LAYERS.broker) return entity.name;
  if (layer === LAYERS.property) return entity.address;
  return entity.registration_number;
};

const searchText = (entity, layer) => {
  if (layer === LAYERS.broker) return entity.name;
  if (layer === LAYERS.property) return entity.address;
  return entity.registration_number;
};

export const matchesQueryAndFilters = (entity, layer, query, filters) => {
  const postalCode = postalCodeOf(entity, layer);
  if (filters.postalCodes.length && !filters.postalCodes.includes(postalCode)) {
    return false;
  }
  if (filters.cities.length && !filters.cities.includes(entity.city)) {
    return false;
  }
  if (filters.regions.length && !filters.regions.includes(entity.region)) {
    return false;
  }
  if (filters.countries.length && !filters.countries.includes(entity.country)) {
    return false;
  }

  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return searchText(entity, layer).toLowerCase().includes(normalized);
};

const sortValue = (entity, layer, field) => {
  if (field === "created") {
    return layer === LAYERS.broker ? entity.added_date : entity.created_at;
  }
  if (field === "insured") {
    if (layer === LAYERS.broker) return brokerStatsById[entity.id].totalInsured;
    if (layer === LAYERS.property) return entity.total_insured_value;
    return entity.vehicle_value;
  }
  if (field === "premium") {
    if (layer === LAYERS.broker) return brokerStatsById[entity.id].totalPremium;
    return entity.premium;
  }
  const stats = brokerStatsById[entity.id];
  if (field === "properties") return stats.propertyCount;
  if (field === "motors") return stats.motorCount;
  return stats.combinedCount;
};

export const sortEntities = (entities, layer, order) => {
  const [field, direction] = order.split(":");
  const factor = direction === "asc" ? 1 : -1;
  return [...entities].sort((left, right) => {
    const leftValue = sortValue(left, layer, field);
    const rightValue = sortValue(right, layer, field);
    if (typeof leftValue === "string") {
      return leftValue.localeCompare(rightValue) * factor;
    }
    return (leftValue - rightValue) * factor;
  });
};

const hasClaim = (entity, layer) => {
  if (layer === LAYERS.property) return (claimsByProperty[entity.id] || []).length > 0;
  if (layer === LAYERS.motor) return (claimsByMotor[entity.id] || []).length > 0;
  return false;
};

export const toFeature = (entity, layer) => ({
  type: "Feature",
  geometry: {
    type: "Point",
    coordinates: coordinatesOf(entity, layer),
  },
  properties: {
    entityId: entity.id,
    entityType: layer,
    label: labelOf(entity, layer),
    color: hasClaim(entity, layer) ? CLAIM_MARKER_COLOR : MARKER_COLORS[layer],
    icon: markerIconId(entity, layer),
  },
});

export const accidentFeatures = (motorIds) =>
  motorClaims
    .filter((claim) => motorIds.has(claim.motor_id))
    .map((claim) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [claim.accident_longitude, claim.accident_latitude],
      },
      properties: {
        entityId: claim.motor_id,
        entityType: "accident",
        claimId: claim.id,
        label: motorsById[claim.motor_id]?.registration_number || claim.claim_number,
        color: CLAIM_MARKER_COLOR,
        icon: "explorer-accident",
      },
    }));

export const toFeatureCollection = (features) => ({
  type: "FeatureCollection",
  features,
});

const entityByType = {
  [LAYERS.broker]: brokersById,
  [LAYERS.property]: propertiesById,
  [LAYERS.motor]: motorsById,
};

export const entityRecord = (type, id) => entityByType[type]?.[id] || null;

export const selectionFeatures = (selection) => {
  if (!selection) return [];
  if (selection.type === LAYERS.broker) {
    const broker = brokersById[selection.id];
    if (!broker) return [];
    return [
      toFeature(broker, LAYERS.broker),
      ...(propertiesByBroker[broker.id] || []).map((property) =>
        toFeature(property, LAYERS.property),
      ),
      ...(motorsByBroker[broker.id] || []).map((motor) =>
        toFeature(motor, LAYERS.motor),
      ),
    ];
  }

  const entity = entityRecord(selection.type, selection.id);
  if (!entity) return [];
  const broker = brokersById[entity.broker_id];
  return [
    toFeature(entity, selection.type),
    ...(broker ? [toFeature(broker, LAYERS.broker)] : []),
  ];
};

const bucketKey = (isoDate, granularity) => {
  if (granularity === "year") return isoDate.slice(0, 4);
  if (granularity === "month") return isoDate.slice(0, 7);
  return isoDate.slice(0, 10);
};

export const brokerChartSeries = (brokerId, granularity) => {
  const buckets = new Map();
  const add = (isoDate, series) => {
    const key = bucketKey(isoDate, granularity);
    if (!buckets.has(key)) buckets.set(key, { properties: 0, motors: 0 });
    buckets.get(key)[series] += 1;
  };

  propertyExposures.forEach((exposure) => {
    const property = propertiesById[exposure.property_id];
    if (property?.broker_id === brokerId) add(exposure.created_at, "properties");
  });
  motorExposures.forEach((exposure) => {
    const motor = motorsById[exposure.motor_id];
    if (motor?.broker_id === brokerId) add(exposure.created_at, "motors");
  });

  return [...buckets.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, counts]) => ({ key, ...counts }));
};
